// "Showing 11–20 of 34" and page buttons, under every portal table. Hidden
// when there is nothing to show (the table's empty state says so).
import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const PER_PAGE = 10;

// 1 … 4 5 [6] 7 8 … 20
const pageList = (page, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, page - 1, page, page + 1].filter((p) => p >= 1 && p <= total));
  const sorted = [...pages].sort((a, b) => a - b);
  return sorted.flatMap((p, i) => (i && p - sorted[i - 1] > 1 ? ["…", p] : [p]));
};

export default function TablePager({ currentPage = 1, setCurrentPage, totalPages = 1, totalItems = 0, perPage = PER_PAGE }) {
  const total = Number(totalItems) || 0;
  if (!total) return null;
  const pages = Math.max(1, Number(totalPages) || 1);
  const first = (currentPage - 1) * perPage + 1;
  const last = Math.min(currentPage * perPage, total);
  const go = (p) => p >= 1 && p <= pages && p !== currentPage && setCurrentPage(p);
  const button = "inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-lg px-2 text-sm";

  return (
    <nav className="mt-3 flex flex-col items-center justify-between gap-3 px-1 sm:flex-row" aria-label="Table pages">
      <p className="text-sm text-gray-500">
        Showing <span className="font-medium text-gray-900">{first}–{last}</span> of <span className="font-medium text-gray-900">{total}</span>
      </p>
      {pages > 1 && (
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => go(currentPage - 1)} disabled={currentPage <= 1} aria-label="Previous page"
            className={`${button} text-gray-600 hover:bg-gray-100 disabled:opacity-40`}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          {pageList(currentPage, pages).map((p, i) =>
            p === "…" ? (
              <span key={`gap-${i}`} className="px-1 text-gray-400">…</span>
            ) : (
              <button key={p} type="button" onClick={() => go(p)} aria-current={p === currentPage ? "page" : undefined}
                className={`${button} ${p === currentPage ? "bg-blue-600 font-semibold text-white" : "text-gray-700 hover:bg-gray-100"}`}>
                {p}
              </button>
            )
          )}
          <button type="button" onClick={() => go(currentPage + 1)} disabled={currentPage >= pages} aria-label="Next page"
            className={`${button} text-gray-600 hover:bg-gray-100 disabled:opacity-40`}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </nav>
  );
}
